import { NavBar } from 'components/NavBar/NavBar'
import { setConfirmModal } from 'features/global/globalSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  Flex,
  MainHeadline,
  PageFooter,
  PageWrapper,
} from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Pagination } from 'components/Pagination/Pagination'
import { getParams, handlePagination } from 'utils/helpers'
import { ModalTypes } from 'types/global.interface'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearFuelSuppliers, deleteFuelSupplier, getFuelSuppliers } from 'features/fuelManagement/fuelManagementSlice'
import { getFuelClaimSummary } from 'features/fuelManagement/fuelCustodySlice'
import { FuelSuppliersModal } from './FuelSuppliersModal'
import { Options } from 'components/Options/Options'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { formatGhs, FUEL_SUPPLIER_FOOTNOTE } from '../Shared/fuelFormatters'
import { SupplierClaimsPanel } from './SupplierClaimsPanel'
import { SupplierCellEm, SupplierOpenClaims } from './FuelSuppliers.styled'

export const FuelSuppliers = () => {
  const [modal, setModal] = useState({ open: false, value: null })
  const dispatch = useDispatch<AppDispatch>()
  const [params, setParams] = useState<any>(getParams({ location }))
  const { suppliers } = useSelector((store: RootState) => store.fuelManagement)
  const { claimSummary } = useSelector((store: RootState) => store.fuelCustody)

  useQueryPagination({ fetchFunctions: [getFuelSuppliers], cleanUpFunctions: [clearFuelSuppliers], params })

  useEffect(() => {
    dispatch(getFuelClaimSummary({}) as any)
  }, [dispatch])

  const openClaimsBySupplier = useMemo(() => {
    const map: { [id: number]: { count: number; valueGhs: number } } = {}
    claimSummary.forEach((s) => {
      map[s.supplierId] = { count: s.count, valueGhs: s.valueGhs }
    })
    return map
  }, [claimSummary])

  const rows = suppliers?.data ?? []

  const handleRemove = (id: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deleteFuelSupplier(id))
        },
        setParams: {
          type: ModalTypes.Delete,
          f: setParams,
          noItems: suppliers.data.length < 2,
        },
        word: 'delete supplier',
      }),
    )
  }

  const columns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'NAME', cell: (item) => <SupplierCellEm>{item.name || '/'}</SupplierCellEm> },
    {
      id: 'openClaims',
      header: 'OPEN CLAIMS',
      cell: (item) => {
        const summary = openClaimsBySupplier[item.id]
        return summary
          ? <SupplierOpenClaims $open>{`${summary.count} · ${formatGhs(summary.valueGhs)}`}</SupplierOpenClaims>
          : <SupplierOpenClaims>—</SupplierOpenClaims>
      },
    },
    { id: 'phoneNumber', header: 'PHONE NUMBER', cell: (item) => item.phoneNumber || '/' },
    { id: 'email', header: 'EMAIL', cell: (item) => item.email || '/' },
    { id: 'address', header: 'ADDRESS', defaultVisible: false, cell: (item) => item.address || '/' },
    { id: 'vatId', header: 'VAT ID', cell: (item) => item.vatId || '/' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (item) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Options item={item} title='Fuel Suppliers' handleRemove={handleRemove} openModal={() => setModal({ open: true, value: item })} />
        </span>
      ),
    },
  ]

  return (
    <>
      <PageWrapper>
        <NavBar defaultValue={params.q} input placeholder='Search Fuel Suppliers...' setParams={setParams} />
        <Flex between mb='16' center>
          <Flex column gap='0.25rem'>
            <MainHeadline>Fuel Suppliers</MainHeadline>
            <OtherText grey>Commercial terms and delivery performance</OtherText>
          </Flex>
          <Button variant='primary' size='sm' onClick={() => setModal({ open: true, value: null })}>
            Add Supplier
          </Button>
        </Flex>
        {!rows.length ? (
          <NoItems title='Fuel Suppliers' />
        ) : (
          <>
            <HonestTable columns={columns} data={rows} keyField='id' columnToggle />
            <Flex mt='16'>
              <OtherText color={tokens.text.muted}>{FUEL_SUPPLIER_FOOTNOTE}</OtherText>
            </Flex>
          </>
        )}
        <Flex mt='16' width='100%'>
          <SupplierClaimsPanel />
        </Flex>
      </PageWrapper>
      {modal.open &&
        <FuelSuppliersModal value={modal.value} closeModal={() => setModal({ value: null, open: false })} />
      }
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams)}
          pageCount={Math.ceil(suppliers.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
